import { ensureDefined } from "common/utils/assertOfType";
import { aabb3BoxDist, aabb3ContainsVec3, aabb3ContainsVec3ExclusiveMax, aabb3Overlaps, aabb3Volume } from "./aabb3";
import type { AABB3 } from "./aabb3Types";
import { aabb3New } from "./aabb3Types";
import { DIST_EPSILON } from "./core";
import type { Vec3 } from "./vec3";
import { vec3Dist } from "./vec3";

/**
 * A general Octree class for storing points.
 *
 * Supports range queries and closest point computations.
 */
export default class Octree {
  capacity: number = 4;

  public children: Octree[];

  points: Vec3[] = [];

  constructor(
    readonly boundary: AABB3,
    public readonly parent?: Octree,
    public readonly ptDistFunc = (a, b) => vec3Dist(a[0], a[1], a[2], b[0], b[1], b[2]),
    public readonly boxDistFunc = (box: AABB3, vec: Vec3) => aabb3BoxDist(box, vec[0], vec[1], vec[2]),
  ) {}

  hasChildren(): boolean {
    return !!this.children;
  }

  findLeafContainingPoint(queryPt: Vec3): Octree {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let current: Octree = this;

    // find the node the query pt is in
    while (current.hasChildren()) {
      for (const child of current.children) {
        if (child.contains(queryPt)) {
          current = child;
          break;
        }
      }
    }

    return current;
  }

  contains(queryPt: Vec3): boolean {
    return aabb3ContainsVec3ExclusiveMax(this.boundary, queryPt[0], queryPt[1], queryPt[2]);
  }

  nodeCount(): number {
    return 1 + (this.children ? this.children.reduce((a, node) => a + node.nodeCount(), 0) : 0);
  }

  volume(): number {
    return aabb3Volume(this.boundary);
  }

  checkInvariants() {
    const thisVolume = this.volume();

    if (this.children) {
      for (const child of this.children) {
        // console.log(thisVolume, child.volume())
        if (!(Math.abs(child.volume() - thisVolume / 8) < DIST_EPSILON)) {
          console.assert(false);
        }
        child.checkInvariants();

        if (!(aabb3Overlaps(this.boundary, child.boundary))) {
          console.assert(false);
        }
      }

      for (let i = 0; i < this.children.length; i++) {
        const a = this.children[i];
        const ax = a.boundary.maxX;
        const ay = a.boundary.maxY;
        const az = a.boundary.maxZ;

        for (let j = i + 1; j < this.children.length; j++) {
          const b = this.children[j];
          if (!(!aabb3ContainsVec3ExclusiveMax(b.boundary, ax, ay, az))) {
            console.assert(false);
          }
        }
      }
    }
  }

  closestPoint(queryPt: Vec3): { pt: Vec3; dist: number } | null {
    const leaf = this.findLeafContainingPoint(queryPt);

    const queue: Octree[] = [leaf];
    let min: { pt: Vec3; dist: number } | null = null;

    const set = new Set<Octree>();
    set.add(leaf);

    while (queue.length) {
      const current = ensureDefined(queue.shift());

      const cand = current.closestPointWithinDistance(queryPt, min?.dist ?? Number.MAX_VALUE);
      if (cand) {
        if (!min || cand.dist < min.dist) {
          min = cand;
        }
      }

      if (current.parent) {
        for (const child of current.parent.children) {
          if (!set.has(child)) {
            queue.push(child);
            set.add(child);
          }
        }

        if (!set.has(current.parent)) {
          queue.push(current.parent);
          set.add(current.parent);
        }
      }
    }

    return min;
  }

  closestPointWithinDistance(queryPt: Vec3, maxDistance: number): { pt: Vec3; dist: number } | null {
    if (this.boxDistFunc(this.boundary, queryPt) > maxDistance) {
      return null;
    }

    if (!this.hasChildren()) {
      let minPt;
      let minDist = maxDistance;

      for (const point of this.points) {
        const dist = vec3Dist(queryPt[0], queryPt[1], queryPt[2], point[0], point[1], point[2]);

        if (dist < minDist) {
          minDist = dist;
          minPt = point;
        }
      }

      if (minPt) {
        return { pt: minPt, dist: minDist };
      }

      return null;
    }

    let min: { pt: Vec3; dist: number } | null = null;

    for (const child of this.children) {
      const res = child.closestPointWithinDistance(queryPt, maxDistance);
      if (!res) {
        continue;
      }
      if (!min || res.dist < min.dist) {
        min = res;
      }
    }

    return min;
  }

  insert(v: Vec3): boolean {
    if (!this.contains(v)) {
      return false;
    }

    if (this.points.length < this.capacity && !this.hasChildren()) {
      this.points.push(v);
      return true;
    }

    if (!this.hasChildren()) {
      this.subdivide();
    }

    for (const child of this.children) {
      if (child.insert(v)) {
        return true;
      }
    }

    return false;
  }

  queryRange(range: AABB3): Vec3[] {
    const pts: Vec3[] = [];

    if (!aabb3Overlaps(this.boundary, range)) {
      return pts;
    }

    for (const pt of this.points) {
      if (aabb3ContainsVec3(range, pt[0], pt[1], pt[2])) {
        pts.push(pt);
      }
    }

    if (!this.hasChildren()) {
      return pts;
    }

    for (const child of this.children) {
      pts.push(...child.queryRange(range));
    }

    return pts;
  }

  private subdivide() {
    if (!(!this.hasChildren())) {
      console.assert(false);
    }

    const { minX, minY, minZ, maxX, maxY, maxZ } = this.boundary;

    const midX = (minX + maxX) / 2;
    const midY = (minY + maxY) / 2;
    const midZ = (minZ + maxZ) / 2;

    this.children = [
      new Octree(aabb3New(minX, minY, midZ, midX, midY, maxZ), this),
      new Octree(aabb3New(midX, minY, midZ, maxX, midY, maxZ), this),

      new Octree(aabb3New(minX, midY, midZ, midX, maxY, maxZ), this),
      new Octree(aabb3New(midX, midY, midZ, maxX, maxY, maxZ), this),

      new Octree(aabb3New(minX, minY, minZ, midX, midY, midZ), this),
      new Octree(aabb3New(midX, minY, minZ, maxX, midY, midZ), this),

      new Octree(aabb3New(minX, midY, minZ, midX, maxY, midZ), this),
      new Octree(aabb3New(midX, midY, minZ, maxX, maxY, midZ), this),
    ];

    for (const pt of this.points) {
      let added = false;
      for (const child of this.children) {
        if (child.insert(pt)) {
          added = true;
          break;
        }
      }

      if (!(added)) {
        console.assert(false, "couldn't insert pt into child nodes");
      }
    }

    this.points = [];
  }
}
